/**
 * Value Rollup — the ROI surface that sells the $1,497/mo back.
 *
 * The value ledger (valueLedger.ts) already computes honest per-action value;
 * this module rolls it up over a DATE RANGE (e.g. the current month) into the
 * exec-forwardable shape a renewal conversation needs: how much was proposed,
 * how much a human approved, what was reversed, the operator hours saved, the
 * dollars touched, who (which agent) produced the value — and whether the
 * underlying decision ledger's hash chain still verifies.
 *
 * Honesty rules:
 *   • Only APPROVED entries contribute hours/dollars/topAgents. A proposal
 *     is intent; a reversed entry was undone — neither inflates the number.
 *   • A reversed entry counts once, in `reversed`, never also in `approved`.
 *   • Strict zero-state: an empty range yields all zeros — NEVER NaN.
 *   • Integrity is READ from the shipped chain verifier (verifyChain), not
 *     re-implemented: 'verified' means the same tamper-evident chain the
 *     store sealed still checks out over the entries passed in.
 *   • Pure and IO-free: all data arrives as arguments; callers own reads.
 */

import { verifyChain, type LedgerEntry } from '../ledger/verifyChain';
import { MINUTES_SAVED_BY_KIND } from './valueLedger';
import type { AgentAction } from './types';
import type { ApprovalItem } from '../db/types';

// Re-exported so consumers (and tests) can build ledger entries from this
// module alone.
export type { LedgerEntry } from '../ledger/verifyChain';

/** Lifecycle of one unit of governed agent work. */
export type ValueEntryStatus = 'proposed' | 'approved' | 'reversed';

/** One unit of agent work, reduced to what the rollup needs. */
export type ValueEntry = {
  id: string;
  /** Producing agent (or source, e.g. 'scheduler'). */
  agent: string;
  /** ISO-8601 UTC timestamp the work was proposed. */
  at: string;
  status: ValueEntryStatus;
  /** Estimated operator minutes the work saved (counted only when approved). */
  minutesSaved: number;
  /** USD the work touched (counted only when approved). Influenced, not banked. */
  dollarImpact: number;
};

/** Inclusive ISO-8601 window, compared lexicographically (UTC ISO sorts). */
export type DateRange = { from: string; to: string };

export type IntegrityStatus = 'verified' | 'broken';

/** Maximum agents listed in the `topAgents` leaderboard. */
export const TOP_AGENTS_LIMIT = 3;

/** The monthly (or any-range) rollup an exec can be forwarded. */
export type ValueRollup = {
  /** Everything the agents put forward in range (all statuses — every entry began as a proposal). */
  proposed: number;
  /** Entries a human approved that STAYED approved (reversed excluded). */
  approved: number;
  /** Approved-then-undone entries. Counted here, never also in `approved`. */
  reversed: number;
  /** Operator hours saved by approved work, rounded to one decimal. */
  hoursSaved: number;
  /** USD touched by approved work. */
  dollarImpact: number;
  /** Read from the shipped hash-chain verifier over the supplied ledger. */
  integrityStatus: IntegrityStatus;
  /** Agents ranked by approved dollar value, descending, top N only. */
  topAgents: { agent: string; value: number }[];
};

/** Coerce a possibly-absent/NaN number to a safe finite value. */
function safe(n: number | undefined | null): number {
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/**
 * Roll up value entries within `range` (inclusive on both ends), stamping
 * chain integrity from the shipped verifier. Strict zero-state: an empty
 * range — or an empty entry list — yields all zeros and an empty
 * leaderboard, never NaN, never a throw.
 */
export function rollupValue(
  valueEntries: ValueEntry[],
  ledgerEntries: LedgerEntry[],
  range: DateRange,
): ValueRollup {
  const inRange = valueEntries.filter((e) => e.at >= range.from && e.at <= range.to);

  let approved = 0;
  let reversed = 0;
  let minutes = 0;
  let dollars = 0;
  const byAgent = new Map<string, number>();

  for (const e of inRange) {
    if (e.status === 'reversed') {
      // Undone work: counted once here, contributes no value.
      reversed += 1;
      continue;
    }
    if (e.status !== 'approved') continue;
    approved += 1;
    minutes += safe(e.minutesSaved);
    const value = safe(e.dollarImpact);
    dollars += value;
    byAgent.set(e.agent, (byAgent.get(e.agent) ?? 0) + value);
  }

  const topAgents = [...byAgent.entries()]
    .map(([agent, value]) => ({ agent, value }))
    .filter((a) => a.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, TOP_AGENTS_LIMIT);

  return {
    proposed: inRange.length,
    approved,
    reversed,
    hoursSaved: Math.round((minutes / 60) * 10) / 10,
    dollarImpact: dollars,
    integrityStatus: verifyChain(ledgerEntries).ok ? 'verified' : 'broken',
    topAgents,
  };
}

/**
 * Adapt approvals-ledger rows to {@link ValueEntry}s. Pending and dismissed
 * items map to 'proposed' (they were put forward but never delivered);
 * approved items to 'approved'. Minutes reuse the SAME transparent per-kind
 * estimates the value ledger publishes; dollars are the figure the action
 * genuinely carries (0 otherwise). Pure — callers do the reading.
 */
export function approvalsToValueEntries(approvals: ApprovalItem[]): ValueEntry[] {
  return approvals.map((item) => {
    const action = item.action as AgentAction;
    // Same kinds the value ledger's dollarsOf counts: actions that genuinely
    // carry a USD figure (deal/contract value, at-risk amount chased).
    const value =
      action.kind === 'create_person' ||
      action.kind === 'create_document' ||
      action.kind === 'create_task'
        ? action.value ?? 0
        : 0;
    return {
      id: item.id,
      agent: item.source,
      at: item.createdAt,
      status: item.status === 'approved' ? 'approved' : 'proposed',
      minutesSaved: MINUTES_SAVED_BY_KIND[action.kind] ?? 0,
      dollarImpact: safe(value),
    };
  });
}
